import { api } from "./client";
import type { ApiResponse } from "./types";

export interface ConnectionUser {
  id: string;
  name: string;
  avatarUrl: string | null;
  city: string | null;
  state: string | null;
  bio?: string | null;
  jobTitle?: string | null;
  industry?: string | null;
  followedAt?: string;
}

export interface ConnectionCounts {
  followers: number;
  following: number;
  pendingRequests: number;
  /** The members you blocked. Never the members who blocked you. */
  blocked: number;
}

export function getCounts() {
  return api.get<ApiResponse<ConnectionCounts>>("/connections/counts");
}

// The connections list endpoints return the user array AS `data`, with
// pagination in a sibling `meta` (not nested under data).
export function getFollowers(params?: { page?: number; limit?: number }) {
  return api.get<ApiResponse<ConnectionUser[]>>(
    "/connections/followers",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function getFollowing(params?: { page?: number; limit?: number }) {
  return api.get<ApiResponse<ConnectionUser[]>>(
    "/connections/following",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function getRequests(params?: { page?: number; limit?: number }) {
  return api.get<ApiResponse<ConnectionUser[]>>(
    "/connections/requests",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function getBlocked(params?: { page?: number; limit?: number }) {
  return api.get<ApiResponse<ConnectionUser[]>>(
    "/connections/blocked",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function acceptRequest(followerId: string) {
  return api.post<ApiResponse<unknown>>(`/connections/requests/${followerId}/accept`, {});
}

export function rejectRequest(followerId: string) {
  return api.post<ApiResponse<unknown>>(`/connections/requests/${followerId}/reject`, {});
}

// What follow answers (201 either way): a new connection carries `id` and
// `needsApproval`; one that already existed carries `alreadyFollowing`.
export interface FollowUserResult {
  id?: string;
  /** Known values: pending, accepted, blocked. */
  status: string;
  needsApproval?: boolean;
  alreadyFollowing?: boolean;
}

// Follow/block actions live under the users module (/users/:id/*), not
// /connections — the connections endpoints are read-only list views.
export function followUser(userId: string) {
  return api.post<ApiResponse<FollowUserResult>>(`/users/${userId}/follow`, {});
}

export function unfollowUser(userId: string) {
  return api.delete<ApiResponse<{ unfollowed: boolean }>>(`/users/${userId}/follow`);
}

export function blockUser(userId: string) {
  return api.post<ApiResponse<{ blocked: boolean }>>(`/users/${userId}/block`, {});
}

export function unblockUser(userId: string) {
  return api.delete<ApiResponse<{ unblocked: boolean }>>(`/users/${userId}/block`);
}
